import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VslHeroVideo } from '@/components/VslHeroVideo';

/** Homepage hero plays the same Vidalytics VSL as /vsl. */
const HeroVideo = () => <VslHeroVideo />;

export const Hero = () => (
    <section className="relative min-h-screen flex flex-col justify-start overflow-hidden">
      {/* Background — soft white aura on dark */}
      <div className="absolute inset-0 bg-background" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 45% 90% at 0% 50%, hsl(0 0% 100% / 0.18) 0%, hsl(0 0% 100% / 0.06) 40%, transparent 75%), radial-gradient(ellipse 45% 90% at 100% 50%, hsl(0 0% 100% / 0.18) 0%, hsl(0 0% 100% / 0.06) 40%, transparent 75%)',
        }}
      />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-background pointer-events-none" />

      {/* Content — text centered, full-width video below (like the reference layout) */}
      <div className="container mx-auto px-4 md:px-8 relative z-10 pt-24 md:pt-28 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="inline-flex items-center gap-2 glass-card !px-5 !py-2.5 mb-6"
          >
            <span className="text-primary text-base md:text-lg font-semibold tracking-wide leading-tight">
              For kitchen remodelers doing $3M+ a year
            </span>
          </motion.div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Get <span className="text-accent">10 Kitchen Remodeling Jobs</span> in <span className="text-accent">90 Days</span> with our Scroll-to-Kitchen System
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            We fill your reps' calendars with qualified homeowners, not price shoppers — or we keep delivering
            appointments free until you do. Without paying for shared leads, waiting for referrals, or managing multiple marketing vendors.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="btn-hero-accent inline-flex items-center justify-center gap-2 min-w-[220px] px-8">
              See if your company qualifies
              <ArrowDown className="w-5 h-5" />
            </a>
            <Link to="/case-studies" className="inline-flex items-center justify-center gap-2 min-w-[200px] px-8 py-3 rounded-lg border-2 border-primary text-primary font-semibold hover:bg-primary/10 transition-all duration-300">
              Proof of work
            </Link>
          </div>
        </motion.div>

        {/* Full-width video below the hero text */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-10 md:mt-14"
        >
          <HeroVideo />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown className="w-6 h-6 text-muted-foreground" />
        </motion.div>
      </motion.div>
    </section>
);
